/**
 * 將快速估價報價單輸出為 .docx
 */
import {
  AlignmentType,
  BorderStyle,
  Document,
  Packer,
  Paragraph,
  Table,
  TableBorders,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType
} from 'docx';

const COL_WIDTHS = [1400, 4466, 1100, 1750, 1750];
const TABLE_WIDTH = COL_WIDTHS.reduce((sum, width) => sum + width, 0);

function textRun(text, options = {}) {
  return new TextRun({
    text: text == null ? '' : String(text),
    bold: Boolean(options.bold),
    size: options.size || 20,
    font: 'Microsoft JhengHei'
  });
}

function paragraph(text, options = {}) {
  return new Paragraph({
    alignment: options.align || AlignmentType.LEFT,
    spacing: { before: options.before ?? 0, after: options.after ?? 80 },
    children: [textRun(text, options)]
  });
}

function cell(text, options = {}) {
  const lines = String(text ?? '').split('\n');
  return new TableCell({
    width: { size: options.width || COL_WIDTHS[1], type: WidthType.DXA },
    columnSpan: options.span,
    margins: { top: 60, bottom: 60, left: 80, right: 80 },
    shading: options.fill ? { fill: options.fill } : undefined,
    children: lines.map((line, index) => new Paragraph({
      alignment: options.align || AlignmentType.LEFT,
      spacing: { after: index === lines.length - 1 ? 0 : 40 },
      children: [textRun(line, { bold: options.bold, size: options.size || 18 })]
    }))
  });
}

function lineRow(kind, name, qty, listAmount, quotedAmount, options = {}) {
  return new TableRow({
    children: [
      cell(kind, { width: COL_WIDTHS[0], bold: options.bold, fill: options.fill }),
      cell(name, { width: COL_WIDTHS[1], bold: options.bold, fill: options.fill }),
      cell(qty, { width: COL_WIDTHS[2], align: AlignmentType.RIGHT, bold: options.bold, fill: options.fill }),
      cell(listAmount, { width: COL_WIDTHS[3], align: AlignmentType.RIGHT, bold: options.bold, fill: options.fill }),
      cell(quotedAmount, { width: COL_WIDTHS[4], align: AlignmentType.RIGHT, bold: options.bold, fill: options.fill, size: options.size })
    ]
  });
}

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const NO_CELL_BORDERS = {
  top: NO_BORDER,
  bottom: NO_BORDER,
  left: NO_BORDER,
  right: NO_BORDER
};
const HEADER_LEFT_WIDTH = 5600;
const HEADER_RIGHT_WIDTH = TABLE_WIDTH - HEADER_LEFT_WIDTH;

function headerCell(paragraphs, width) {
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    borders: NO_CELL_BORDERS,
    verticalAlign: VerticalAlign.CENTER,
    margins: { top: 0, bottom: 80, left: 0, right: 0 },
    children: paragraphs
  });
}

function summaryRow(label, amount, options = {}) {
  return new TableRow({
    children: [
      cell(label, {
        width: COL_WIDTHS[0] + COL_WIDTHS[1] + COL_WIDTHS[2] + COL_WIDTHS[3],
        span: 4,
        bold: options.bold,
        fill: options.fill || 'F8F9FA'
      }),
      cell(amount, {
        width: COL_WIDTHS[4],
        align: AlignmentType.RIGHT,
        bold: true,
        fill: options.fill || 'F8F9FA',
        size: options.size || 18
      })
    ]
  });
}

/**
 * @param {object} model 已格式化的報價單文字
 * @returns {Promise<Blob>}
 */
export async function buildQuoteDocx(model) {
  const rows = [
    new TableRow({
      tableHeader: true,
      children: ['項目', '說明', '數量', '表價（未稅）', '報價（未稅）'].map((label, index) => cell(label, {
        width: COL_WIDTHS[index],
        bold: true,
        fill: 'F1F3F5',
        align: index >= 2 ? AlignmentType.RIGHT : AlignmentType.LEFT
      }))
    })
  ];

  for (const item of model.subscriptionItems || []) {
    rows.push(lineRow('訂閱／月', item.name, item.qty, item.listAmount, item.quotedAmount));
  }
  rows.push(summaryRow(model.billingPeriodLabel, model.billingPeriodAmount));

  for (const item of model.serviceItems || []) {
    rows.push(lineRow(item.kind, item.name, item.qty, item.listAmount, item.quotedAmount));
  }
  if ((model.serviceItems || []).length) {
    rows.push(summaryRow('單次／加購小計', model.serviceFeeTotal));
  }

  rows.push(summaryRow('首年未稅小計', model.subtotal, { bold: true }));
  rows.push(summaryRow(model.taxLabel, model.tax, { bold: true }));
  rows.push(summaryRow('首年含稅總計', model.total, { bold: true, fill: 'E9ECEF', size: 24 }));

  const children = [
    new Table({
      width: { size: TABLE_WIDTH, type: WidthType.DXA },
      columnWidths: [HEADER_LEFT_WIDTH, HEADER_RIGHT_WIDTH],
      borders: TableBorders.NONE,
      rows: [
        new TableRow({
          children: [
            headerCell([
              paragraph('LOCKLINE+', { bold: true, size: 32, after: 40 }),
              paragraph('產品報價單', { bold: true, size: 28, after: 0 })
            ], HEADER_LEFT_WIDTH),
            headerCell([
              ...(model.quoteNo
                ? [paragraph(`${model.quoteNo}　第 ${model.versionNo || 1} 版`, { align: AlignmentType.RIGHT, size: 20, after: 40 })]
                : []),
              paragraph(`報價日期：${model.quoteDate || '—'}`, { align: AlignmentType.RIGHT, size: 20, after: 40 }),
              paragraph(`有效期限：${model.validUntil || '—'}`, { align: AlignmentType.RIGHT, size: 20, after: 40 }),
              paragraph(`價目版本：v${model.catalogVersion || '—'}`, { align: AlignmentType.RIGHT, size: 20, after: 0 })
            ], HEADER_RIGHT_WIDTH)
          ]
        })
      ]
    }),
    paragraph(`客戶抬頭：${model.customerName || '—'}` , { bold: true, before: 160, after: 40 })
  ];

  if (model.taxId) children.push(paragraph(`統一編號：${model.taxId}`));
  if (model.address) children.push(paragraph(`地址：${model.address}`));
  if (model.contactName) children.push(paragraph(`聯絡人：${model.contactName}`));
  if (model.contactPhone) children.push(paragraph(`電話：${model.contactPhone}`));
  if (model.contactEmail) children.push(paragraph(`Email：${model.contactEmail}`));
  children.push(paragraph(`業務窗口：${model.salespersonName || '—'}` , { after: 200 }));

  children.push(new Table({
    width: { size: TABLE_WIDTH, type: WidthType.DXA },
    columnWidths: COL_WIDTHS,
    rows
  }));

  if (model.deviatedNote) {
    children.push(paragraph(model.deviatedNote, { size: 18, after: 80 }));
  }
  if (model.notes) {
    children.push(paragraph('備註', { bold: true, after: 40 }));
    children.push(paragraph(model.notes, { after: 120 }));
  }
  if ((model.terms || []).length) {
    children.push(paragraph('條款說明', { bold: true, after: 40 }));
    model.terms.forEach((term, index) => {
      children.push(paragraph(`${index + 1}. ${term}`, { size: 18, after: 40 }));
    });
  }
  if (model.foot) {
    children.push(paragraph(model.foot, { size: 18, after: 0 }));
  }

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          size: { width: 11906, height: 16838 },
          margin: { top: 720, right: 720, bottom: 720, left: 720 }
        }
      },
      children
    }]
  });

  return Packer.toBlob(doc);
}

/**
 * 經銷價估價單 .docx
 * @param {object} model
 * @returns {Promise<Blob>}
 */
export async function buildDealerShareDocx(model) {
  const pricePairRow = (label, selling, dealerPrice, options = {}) => new TableRow({
    children: [
      cell(label, {
        width: COL_WIDTHS[0] + COL_WIDTHS[1],
        span: 2,
        bold: options.bold,
        fill: options.fill || 'F8F9FA'
      }),
      cell(selling, {
        width: COL_WIDTHS[2],
        align: AlignmentType.RIGHT,
        bold: true,
        fill: options.fill || 'F8F9FA',
        size: options.size
      }),
      cell('', { width: COL_WIDTHS[3], fill: options.fill || 'F8F9FA' }),
      cell(dealerPrice, {
        width: COL_WIDTHS[4],
        align: AlignmentType.RIGHT,
        bold: true,
        fill: options.fill || 'F8F9FA',
        size: options.size
      })
    ]
  });

  const rows = [
    new TableRow({
      tableHeader: true,
      children: ['項目', '說明', model.sellingPriceHeader || '售價（未稅）', '折扣', '經銷價（未稅）'].map((label, index) => cell(label, {
        width: COL_WIDTHS[index],
        bold: true,
        fill: 'F1F3F5',
        align: index >= 2 ? AlignmentType.RIGHT : AlignmentType.LEFT
      }))
    })
  ];

  for (const item of model.subscriptionItems || []) {
    rows.push(lineRow('訂閱', item.name, item.basis, item.rate, item.share));
  }
  if ((model.subscriptionItems || []).length) {
    rows.push(pricePairRow('本約訂閱', model.subscriptionBasis, model.subscriptionShare));
  }
  for (const item of model.serviceItems || []) {
    rows.push(lineRow(item.kind, item.name, item.basis, item.rate, item.share));
  }
  if ((model.serviceItems || []).length) {
    rows.push(pricePairRow('單次／年費', model.serviceBasis, model.serviceShare));
  }
  rows.push(pricePairRow('首年合計（未稅）', model.firstYearBasis, model.firstYearShare, { bold: true, fill: 'E9ECEF' }));
  rows.push(pricePairRow('首年合計（含稅）', model.firstYearBasisIncl, model.firstYearShareIncl, { bold: true, fill: 'E9ECEF', size: 24 }));

  const children = [
    new Table({
      width: { size: TABLE_WIDTH, type: WidthType.DXA },
      columnWidths: [HEADER_LEFT_WIDTH, HEADER_RIGHT_WIDTH],
      borders: TableBorders.NONE,
      rows: [
        new TableRow({
          children: [
            headerCell([
              paragraph('LOCKLINE+', { bold: true, size: 32, after: 40 }),
              paragraph('經銷價估價單', { bold: true, size: 28, after: 0 })
            ], HEADER_LEFT_WIDTH),
            headerCell([
              ...(model.quoteNo
                ? [paragraph(`${model.quoteNo}　第 ${model.versionNo || 1} 版`, { align: AlignmentType.RIGHT, size: 20, after: 40 })]
                : []),
              paragraph(`報價日期：${model.quoteDate || '—'}`, { align: AlignmentType.RIGHT, size: 20, after: 40 }),
              paragraph(`有效期限：${model.validUntil || '—'}`, { align: AlignmentType.RIGHT, size: 20, after: 40 }),
              paragraph(`價目版本：v${model.catalogVersion || '—'}`, { align: AlignmentType.RIGHT, size: 20, after: 0 })
            ], HEADER_RIGHT_WIDTH)
          ]
        })
      ]
    }),
    paragraph(`客戶抬頭：${model.customerName || '—'}`, { bold: true, before: 160, after: 40 }),
    paragraph(model.parameterSummary || '', { size: 18, after: 160 })
  ];

  children.push(new Table({
    width: { size: TABLE_WIDTH, type: WidthType.DXA },
    columnWidths: COL_WIDTHS,
    rows
  }));

  for (const note of model.footnotes || []) {
    children.push(paragraph(note, { size: 18, after: 40 }));
  }
  if (model.notes) {
    children.push(paragraph('備註', { bold: true, before: 80, after: 40 }));
    children.push(paragraph(model.notes, { after: 80 }));
  }
  if (model.foot) {
    children.push(paragraph(model.foot, { size: 18, before: 80, after: 0 }));
  }

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          size: { width: 11906, height: 16838 },
          margin: { top: 720, right: 720, bottom: 720, left: 720 }
        }
      },
      children
    }]
  });

  return Packer.toBlob(doc);
}
